// /settings — font-size + speech-rate + language switcher.
// Persists to localStorage and applies chassis setFontScale + setSelectedLang.

import type { RouteRenderer } from "../types.ts";
import {
  setFontScale,
  setSelectedLang,
  getSelectedLang,
  makeBilingual,
  type FontScale,
  type LangCode,
} from "../chassis/elder/index.ts";
import { i18n } from "../chassis/elder/i18n-strings.ts";
import { navigate } from "../router.ts";

const FONT_SCALE_KEY = "empire.elder.font.scale";
const SPEECH_RATE_KEY = "empire.elder.speech.rate";

type SpeechRate = "slow" | "normal" | "fast";

function getFontScale(): FontScale {
  if (typeof localStorage === "undefined") return 1.0;
  const v = parseFloat(localStorage.getItem(FONT_SCALE_KEY) ?? "1.0");
  const allowed: readonly FontScale[] = [1.0, 1.25, 1.5, 2.0];
  return (allowed.find((s) => Math.abs(s - v) < 0.001) ?? 1.0) as FontScale;
}

function persistFontScale(s: FontScale): void {
  if (typeof localStorage !== "undefined") localStorage.setItem(FONT_SCALE_KEY, String(s));
  setFontScale(s);
}

function getSpeechRate(): SpeechRate {
  if (typeof localStorage === "undefined") return "normal";
  const v = localStorage.getItem(SPEECH_RATE_KEY);
  if (v === "slow" || v === "fast") return v;
  return "normal";
}

function persistSpeechRate(r: SpeechRate): void {
  if (typeof localStorage !== "undefined") localStorage.setItem(SPEECH_RATE_KEY, r);
}

export const renderSettings: RouteRenderer = (host) => {
  const lang = getSelectedLang();
  const shell = document.createElement("div");
  shell.className = "sarathi-route-shell";

  const headerEl = document.createElement("header");
  const back = document.createElement("button");
  back.type = "button";
  back.style.cssText = "min-width:88px;min-height:88px;font-size:32px;background:transparent;border:3px solid var(--sarathi-ink);border-radius:14px;cursor:pointer;font-weight:700";
  back.textContent = "←";
  back.addEventListener("click", () => navigate("home"));
  const headingWrap = document.createElement("div");
  const h1 = document.createElement("h1");
  h1.textContent = "मेरी सेटिंग्स";
  const sub = document.createElement("span");
  sub.className = "secondary";
  sub.textContent = "My settings";
  headingWrap.appendChild(h1);
  headingWrap.appendChild(sub);
  headerEl.appendChild(back);
  headerEl.appendChild(headingWrap);
  shell.appendChild(headerEl);

  // Font size card
  shell.appendChild(renderFontScaleCard(lang));
  // Speech rate card
  shell.appendChild(renderSpeechRateCard(lang));
  // Language card
  shell.appendChild(renderLanguageCard());
  // Re-onboard
  shell.appendChild(renderReonboardCard());

  host.appendChild(shell);

  return () => shell.remove();
};

function renderFontScaleCard(lang: LangCode): HTMLElement {
  const card = document.createElement("section");
  card.className = "sarathi-card";
  card.innerHTML = `<h2>लिखावट का आकार</h2><span class="sarathi-secondary-label">Text size</span>`;
  const row = document.createElement("div");
  row.className = "sarathi-row";
  row.style.marginTop = "14px";
  const current = getFontScale();
  const options: ReadonlyArray<{ scale: FontScale; key: string }> = [
    { scale: 1.0, key: "settings.font.bada" },
    { scale: 1.25, key: "settings.font.aur_bada" },
    { scale: 1.5, key: "settings.font.sabse_bada" },
  ];
  for (const o of options) {
    const btn = document.createElement("button");
    btn.type = "button";
    const isActive = Math.abs(current - o.scale) < 0.001;
    btn.style.cssText = `min-height:96px;padding:14px;background:${isActive ? "var(--sarathi-saffron)" : "#fff"};color:${isActive ? "#fff" : "var(--sarathi-ink)"};border:3px solid ${isActive ? "var(--sarathi-saffron-dark)" : "var(--sarathi-rule)"};border-radius:14px;font-size:${Math.round(20 * o.scale)}px;font-weight:700;cursor:pointer;font-family:inherit`;
    btn.innerHTML = `<div>${escapeHtml(makeBilingual(i18n, o.key, lang).primary)}</div><div style="font-size:14px;color:${isActive ? "rgba(255,255,255,.85)" : "var(--sarathi-ash)"};font-weight:500;margin-top:4px">${escapeHtml(makeBilingual(i18n, o.key, "en").primary)}</div>`;
    btn.addEventListener("click", () => {
      persistFontScale(o.scale);
      navigate("settings");
    });
    row.appendChild(btn);
  }
  card.appendChild(row);
  return card;
}

function renderSpeechRateCard(lang: LangCode): HTMLElement {
  const card = document.createElement("section");
  card.className = "sarathi-card";
  card.innerHTML = `<h2>आवाज़ की रफ़्तार</h2><span class="sarathi-secondary-label">Voice speed</span>`;
  const row = document.createElement("div");
  row.className = "sarathi-row";
  row.style.marginTop = "14px";
  const current = getSpeechRate();
  const options: ReadonlyArray<{ rate: SpeechRate; key: string }> = [
    { rate: "slow", key: "settings.speech.dhire" },
    { rate: "normal", key: "settings.speech.normal" },
    { rate: "fast", key: "settings.speech.tez" },
  ];
  for (const o of options) {
    const btn = document.createElement("button");
    btn.type = "button";
    const isActive = current === o.rate;
    btn.style.cssText = `min-height:96px;padding:14px;background:${isActive ? "var(--sarathi-saffron)" : "#fff"};color:${isActive ? "#fff" : "var(--sarathi-ink)"};border:3px solid ${isActive ? "var(--sarathi-saffron-dark)" : "var(--sarathi-rule)"};border-radius:14px;font-size:22px;font-weight:700;cursor:pointer;font-family:inherit`;
    btn.innerHTML = `<div>${escapeHtml(makeBilingual(i18n, o.key, lang).primary)}</div><div style="font-size:14px;color:${isActive ? "rgba(255,255,255,.85)" : "var(--sarathi-ash)"};font-weight:500;margin-top:4px">${escapeHtml(makeBilingual(i18n, o.key, "en").primary)}</div>`;
    btn.addEventListener("click", () => {
      persistSpeechRate(o.rate);
      navigate("settings");
    });
    row.appendChild(btn);
  }
  card.appendChild(row);
  return card;
}

function renderLanguageCard(): HTMLElement {
  const card = document.createElement("section");
  card.className = "sarathi-card";
  card.innerHTML = `<h2>भाषा बदलें</h2><span class="sarathi-secondary-label">Change language</span>`;
  const row = document.createElement("div");
  row.style.cssText = "display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:12px;margin-top:14px";
  const langs: ReadonlyArray<{ code: LangCode; native: string; en: string }> = [
    { code: "hi", native: "हिन्दी", en: "Hindi" },
    { code: "ta", native: "தமிழ்", en: "Tamil" },
    { code: "bn", native: "বাংলা", en: "Bengali" },
    { code: "mr", native: "मराठी", en: "Marathi" },
    { code: "gu", native: "ગુજરાતી", en: "Gujarati" },
    { code: "en", native: "English", en: "English" },
  ];
  const current = getSelectedLang();
  for (const l of langs) {
    const btn = document.createElement("button");
    btn.type = "button";
    const isActive = current === l.code;
    btn.style.cssText = `min-height:96px;padding:12px;background:${isActive ? "var(--sarathi-saffron)" : "#fff"};color:${isActive ? "#fff" : "var(--sarathi-ink)"};border:3px solid ${isActive ? "var(--sarathi-saffron-dark)" : "var(--sarathi-rule)"};border-radius:14px;font-size:22px;font-weight:700;cursor:pointer;font-family:inherit`;
    btn.innerHTML = `${escapeHtml(l.native)}<div style="font-size:14px;color:${isActive ? "rgba(255,255,255,.85)" : "var(--sarathi-ash)"};font-weight:500;margin-top:4px">${escapeHtml(l.en)}</div>`;
    btn.addEventListener("click", () => {
      setSelectedLang(l.code);
      navigate("settings");
    });
    row.appendChild(btn);
  }
  card.appendChild(row);
  return card;
}

function renderReonboardCard(): HTMLElement {
  const card = document.createElement("section");
  card.className = "sarathi-card";
  card.innerHTML = `<h2>Setup phir se</h2><span class="sarathi-secondary-label">Re-run onboarding (keeps existing data)</span>`;
  const btn = document.createElement("button");
  btn.type = "button";
  btn.style.cssText = "margin-top:14px;min-height:88px;padding:0 24px;background:transparent;color:var(--sarathi-ink);border:3px solid var(--sarathi-ink);border-radius:14px;font-size:20px;font-weight:700;cursor:pointer";
  btn.textContent = "Setup khol kar dobara dekhain";
  btn.addEventListener("click", () => navigate("onboard"));
  card.appendChild(btn);
  return card;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    const map: Record<string, string> = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };
    return map[c] ?? c;
  });
}
